import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Popper from '@mui/material/Popper';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import ContactsOutlinedIcon from '@mui/icons-material/ContactsOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ExtensionOutlinedIcon from '@mui/icons-material/ExtensionOutlined';
import axiosInstance from './axiosInstance';


import { useTheme } from '@mui/material/styles';


const typeIcons = {
  lead:    <PersonSearchOutlinedIcon sx={{ fontSize: 16 }} />,
  contact: <ContactsOutlinedIcon sx={{ fontSize: 16 }} />,
  account: <BusinessOutlinedIcon sx={{ fontSize: 16 }} />,
  project: <ExtensionOutlinedIcon sx={{ fontSize: 16 }} />, 
};

const typeLabels = {
  lead:    'Lead',
  contact: 'Kontakt',
  account: 'Unternehmen',
  project: 'Projekt',
};

const GlobalSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const anchorRef = useRef(null);
  const navigate = useNavigate();

  const theme = useTheme();

  useEffect(() => {
    if (query.length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    const timeout = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(`/search/?q=${encodeURIComponent(query)}`);
        setResults(res.data);
        setOpen(true);
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setLoading(false);
      }
    }, 250);

    return () => clearTimeout(timeout);
  }, [query]);

  const handleSelect = (url) => {
    navigate(url);
    setQuery('');
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
        <Box
        ref={anchorRef}
        sx={{
            position: 'relative',
            width: '100%',
            maxWidth: { xs: '100%', sm: 360, md: 480 }, // 👈 statt fix 480
        }}
        >

        {/* Input */}
        <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        bgcolor: 'action.hover',
        borderRadius: 6,
        border: '1px solid', borderColor: 'divider',
        px: 2, py: 0.5,
        '&:hover': {
            borderColor: 'black',
          },
        '&:focus-within': {
            border: '2px solid',
            borderColor: 'primary.main',
        },
        }}>
          {loading
            ? <CircularProgress size={16} sx={{ color: 'text.secondary', flexShrink: 0 }} />
            : <SearchIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
          }
          <InputBase
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => results.length > 0 && setOpen(true)}
            placeholder="Suchen ..."
            sx={{
            color: 'text.primary',       // ← statt white
            fontSize: '0.875rem', width: '100%',
            }}
          />
        </Box>

        {/* Dropdown */}
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          sx={{ zIndex: 1400, width: 320 }}
        >
          <Paper elevation={4} sx={{ mt: 0.5, borderRadius: 2, overflow: 'hidden' }}>
            {results.length === 0 && !loading && (
              <Typography sx={{ p: 2, fontSize: '0.875rem', color: 'text.secondary' }}>
                Keine Ergebnisse für „{query}"
              </Typography>
            )}

            {results.map((r) => (
              <Box
                key={`${r.type}-${r.id}`}
                onClick={() => handleSelect(r.url)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 2, py: 1.25, cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                  borderBottom: '1px solid', borderColor: 'divider',
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Box sx={{ color: 'text.secondary', flexShrink: 0 }}>
                  {typeIcons[r.type]}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }} noWrap>
                    {r.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }} noWrap>
                    {r.subtitle}
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled', flexShrink: 0 }}>
                  {typeLabels[r.type]}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Popper>

      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearch;